import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import { TABLES } from '../../lib/constants';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { motion, AnimatePresence } from 'framer-motion';

const { FiSearch, FiUser, FiSend, FiRefreshCw, FiGlobe, FiCheck, FiAlertCircle, FiX, FiClock } = FiIcons;

const PilotDiscovery = () => {
  const { user } = useAuth();
  const [allPilots, setAllPilots] = useState([]);
  const [contractStatuses, setContractStatuses] = useState({}); // pilot_id -> 'pending' | 'active' | null
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState(null);

  useEffect(() => {
    if (user?.id) fetchDirectory();
  }, [user?.id]);

  const notify = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  };

  const fetchDirectory = async () => {
    setLoading(true);
    try {
      const { data: pilots } = await supabase.from(TABLES.USERS).select('*').eq('role', 'pilot');
      const { data: myContracts } = await supabase.from(TABLES.PILOT_MASTERS).select('pilot_id, status').eq('master_id', user.id);
      
      const statusMap = {};
      (myContracts || []).forEach(c => { statusMap[c.pilot_id] = c.status; });
      
      setAllPilots(pilots || []);
      setContractStatuses(statusMap);
    } catch (err) {
      notify(err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async (pilot) => {
    const pilotName = pilot.nickname || pilot.username;
    setLoading(true);
    try {
      const { error } = await supabase.from(TABLES.PILOT_MASTERS).insert({ 
        pilot_id: pilot.id,
        master_id: user.id,
        status: 'pending',
        monthly_cap: 0
      });
      
      if (error) throw error;
      
      notify(`Invitation sent to ${pilotName}!`);
      setContractStatuses(prev => ({ ...prev, [pilot.id]: 'pending' }));
    } catch (err) {
      notify("Invitation failed: " + err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const filteredDiscovery = allPilots.filter(p => {
    const search = searchTerm.toLowerCase();
    return (p.username || '').toLowerCase().includes(search) || (p.nickname || '').toLowerCase().includes(search);
  });

  return (
    <div className="space-y-12 pb-20 relative">
      <AnimatePresence>
        {notification && (
          <motion.div initial={{ opacity: 0, y: -50 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.9 }} className={`fixed top-6 right-6 z-[100] px-6 py-4 rounded-2xl shadow-2xl border flex items-center gap-3 backdrop-blur-md ${notification.type === 'success' ? 'bg-emerald-500/90 text-white border-emerald-400' : 'bg-red-500/90 text-white border-red-400'}`}>
            <SafeIcon icon={notification.type === 'success' ? FiCheck : FiAlertCircle} />
            <span className="font-black text-xs uppercase tracking-widest">{notification.message}</span>
          </motion.div>
        )}
      </AnimatePresence>

      <header className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <h2 className="text-4xl font-black text-gray-900 tracking-tight flex items-center gap-3">
            <FiGlobe className="text-blue-600" /> Pilot Discovery
          </h2>
          <p className="text-gray-400 font-bold mt-2">Find and invite pilots. Pilots must approve your invitation to join your team.</p>
        </div>
        <button onClick={fetchDirectory} className="p-4 bg-white rounded-2xl text-gray-400 hover:text-blue-600 transition-all shadow-sm border border-gray-100 flex items-center gap-2 font-black text-xs uppercase">
          <FiRefreshCw className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </header>

      <div className="relative">
        <FiSearch className="absolute left-6 top-1/2 -translate-y-1/2 text-gray-400 text-xl" />
        <input type="text" placeholder="Search by @username or nickname..." className="apple-input !pl-16 !py-6 shadow-sm border border-white" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {filteredDiscovery.map(p => {
          const status = contractStatuses[p.id];
          return (
            <div key={p.id} className={`bento-card group transition-all border-2 ${status === 'active' ? 'border-emerald-100 bg-emerald-50/10' : status === 'pending' ? 'border-amber-100 bg-amber-50/10' : 'border-transparent hover:border-blue-100'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-sm ${status === 'active' ? 'bg-emerald-500 text-white' : status === 'pending' ? 'bg-amber-500 text-white' : 'bg-blue-50 text-blue-600'}`}>
                    <FiUser className="text-xl" />
                  </div>
                  <div>
                    <h4 className="font-black text-lg text-gray-900 truncate max-w-[140px]">{p.nickname || p.username}</h4>
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">@{p.username}</p>
                  </div>
                </div>
                {status === 'active' ? (
                  <div className="flex items-center gap-2 text-emerald-600 font-black text-[10px] uppercase bg-white px-3 py-2 rounded-xl border border-emerald-100 shadow-sm">
                    <FiCheck /> In Team
                  </div>
                ) : status === 'pending' ? (
                  <div className="flex items-center gap-2 text-amber-600 font-black text-[10px] uppercase bg-white px-3 py-2 rounded-xl border border-amber-100 shadow-sm">
                    <FiClock /> Invited
                  </div>
                ) : (
                  <button onClick={() => handleInvite(p)} disabled={loading} className="p-3 bg-blue-600 text-white rounded-2xl shadow-lg hover:scale-110 active:scale-95 transition-all disabled:opacity-50 flex items-center gap-2 px-5">
                    <FiSend /> <span className="text-[10px] font-black uppercase">Invite</span>
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PilotDiscovery;